import React, {useContext} from 'react'
import * as PropTypes from 'prop-types'
import {Trans, I18nContext, getI18n} from '@modifi/plugin-i18n'
import {useSsrEffect} from '@modifi/client-ssr'
import {Container, FullScreenLoader} from '@modifi/ui'
import {Helmet} from 'react-helmet'
import {InferPropTypes} from '@modifi/typescript-utils'
import {getLocaleForSocratesLocale} from '@modifi/website-client'

import {componentMap} from './component-map'
import {Wrapper} from './styles'

const propTypes = {
  pageType: PropTypes.string.isRequired,
  pageSlug: PropTypes.string.isRequired,
  pageContent: PropTypes.shape({
    locale: PropTypes.string,
    fields: PropTypes.shape({
      content: PropTypes.string,
      seo: PropTypes.shape({
        seoTitle: PropTypes.string,
        metaDescription: PropTypes.string,
      }),
    }),
    updated: PropTypes.string,
    published: PropTypes.string,
    alternativeLocales: PropTypes.arrayOf(PropTypes.string.isRequired),
  }),
  isLoading: PropTypes.bool.isRequired,
  fetch: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
}
const defaultProps = {
  pageContent: undefined,
}

export type RemotePageProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const CmsPage: React.FC<RemotePageProps> = ({
  pageType,
  pageSlug,
  pageContent,
  fetch,
  isLoading,
  locale,
}) => {
  const {i18n: i18nFromContext} = useContext(I18nContext) || {}
  const i18n = i18nFromContext || getI18n()

  const mappedLocale = pageContent?.locale ? getLocaleForSocratesLocale(pageContent?.locale) : ''

  useSsrEffect(() => {
    fetch(pageType, pageSlug, locale)
  }, [fetch, pageType, pageSlug, locale])

  if (i18n?.options?.react) {
    i18n.options.react.hashTransKey = () => false
  }

  return (
    <>
      <Helmet>
        {pageContent?.fields?.seo?.seoTitle && <title>{pageContent.fields.seo.seoTitle}</title>}
        {pageContent?.fields?.seo?.metaDescription && (
          <meta name="description" content={pageContent?.fields?.seo?.metaDescription} />
        )}
      </Helmet>
      {/* @ts-expect-error types not generated properly after prop-types package upgrade */}
      <Container mt={108}>
        {isLoading && <FullScreenLoader />}
        <Wrapper>
          <div lang={mappedLocale}>
            {pageContent?.fields?.content && (
              <Trans
                i18n={i18n}
                t={i18n?.t}
                defaults={pageContent.fields.content || ''}
                components={componentMap}
                values={{
                  updated: pageContent.published || pageContent.updated,
                }}
              />
            )}
          </div>
        </Wrapper>
      </Container>
    </>
  )
}

CmsPage.propTypes = propTypes
CmsPage.defaultProps = defaultProps

export default CmsPage
